import React from 'react';
import { Heading, TagBadge, Text } from '@chatsdet/ui';
import type { AgentEvalAttempt, AgentEvalGrade, AgentEvalTranscriptEntry } from '@chatsdet/types/agent-eval-result';
import type { AgentEvalAttemptDetail } from '../../../features/agent-evals/useAgentEvalRun';
import { formatCostCents, formatTokens } from '../../../features/agent-evals/agentEvalPresentation';
import { formatDurationMs } from '../../../lib/formatDuration';

export const Pre = ({ label, children }: { label: string; children: string }) => (
  <pre
    aria-label={label}
    className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted p-3 font-mono text-xs"
  >
    {children}
  </pre>
);

export function Transcript({ entries }: { entries: AgentEvalTranscriptEntry[] }) {
  if (entries.length === 0) return null;
  return (
    <ol aria-label="Transcript" className="space-y-2">
      {entries.map((entry, i) => (
        <li key={i} className="space-y-0.5">
          <Text size="xs" className="uppercase tracking-wide text-muted-foreground">{entry.role}</Text>
          <Text size="sm" className="whitespace-pre-wrap text-pretty">{entry.text}</Text>
        </li>
      ))}
    </ol>
  );
}

function GradeRow({ grade }: { grade: AgentEvalGrade }) {
  return (
    <li className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <Text size="sm" className="font-medium">{grade.grader}</Text>
        <TagBadge name={grade.pass ? 'Pass' : 'Fail'} color={grade.pass ? 'green' : 'red'} size="sm" />
        <Text size="sm" className="tabular-nums text-muted-foreground">
          {grade.score === null ? 'grader error' : `score ${grade.score} (${grade.scale})`}
        </Text>
        {grade.judgeModel && <Text size="sm" className="text-muted-foreground">judge {grade.judgeModel}</Text>}
      </div>
      {grade.error && <Text size="sm" className="text-destructive">{grade.error}</Text>}
      <Text size="sm" className="whitespace-pre-wrap text-pretty">{grade.reason}</Text>
    </li>
  );
}

/** One attempt: status, metrics, grades with their reasoning, the answer, and the evidence for a broken run. */
export function AgentEvalAttemptPanel({
  attempt,
  detail,
}: {
  attempt: AgentEvalAttempt;
  detail: AgentEvalAttemptDetail | undefined;
}) {
  const failed = attempt.status === 'error';
  return (
    <section className="space-y-3 py-3 first:pt-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Heading level={4}>{`Attempt ${attempt.index}`}</Heading>
        <TagBadge
          name={failed ? 'Error' : attempt.passed ? 'Pass' : 'Fail'}
          color={!failed && attempt.passed ? 'green' : 'red'}
          size="sm"
        />
        <Text size="sm" className="tabular-nums text-muted-foreground">
          {`${formatDurationMs(attempt.durationMs)} · ${formatCostCents(attempt.costCents, !attempt.costComplete)} · ${formatTokens(attempt.tokens)}`}
        </Text>
      </div>
      {attempt.error && <Text size="sm" className="text-destructive">{attempt.error}</Text>}
      {attempt.grades.length > 0 && (
        <ul className="space-y-3">
          {attempt.grades.map((grade) => (
            <GradeRow key={grade.grader} grade={grade} />
          ))}
        </ul>
      )}
      {detail?.answer && <Pre label="Answer">{detail.answer}</Pre>}
      {failed && detail?.stdoutTail && <Pre label="Stdout tail">{detail.stdoutTail}</Pre>}
      {detail?.transcript && <Transcript entries={detail.transcript} />}
    </section>
  );
}
